






import {
    Advisor, ChoiceQuestion,
    Component,
    ComponentConfig, FileUploadQuestion,
    FlowStep,
    FlowStepType, isRecommendationBundles, Marking,
    ModelProp, NumericValueQuestion, OrderedCategoryInfo, ProductRecommendation, Question, QuestionTypeChecker,
    Recommendation,
    Vue,
} from "@zoovu/runner-browser-api";
import {RecommendationCluster} from "@zoovu/runner-browser-api/build/runner-core-api/src/recommendation/recommendation-cluster";
import {
    PdfConfiguration,
    RecommendationConfiguration,
} from "../configuration";
import VRuntimeTemplate from "v-runtime-template";

interface AdditionalClusterData {
    classList: string;
    clusterHeadline: string;
    clusterSubHeadline: string;
}

type RecommendationClusterExtended = RecommendationCluster & AdditionalClusterData;
type NullableProductRecommendation = ProductRecommendation | null;

@Component({
    components: {VRuntimeTemplate}
})
export default class PdfContentView extends Vue {
    @ComponentConfig(PdfConfiguration)
    componentConfiguration: PdfConfiguration;

    @ComponentConfig(RecommendationConfiguration)
    recommendationConfiguration: RecommendationConfiguration;

    @ModelProp()
    advisor!: Advisor;

    get sanitizedPdfTemplate(): string {
        const pdfTemplates = this.isBundle ? this.componentConfiguration.bundlePdfTemplates : this.componentConfiguration.pdfTemplates;
        const {header, body, footer} =  this.componentConfiguration && pdfTemplates || {};
        const fullTemplate = `${header || ""}${body || ""}${footer || ""}`;
        const pdfContent = fullTemplate
            .replace(/v-html/g, "v-dompurify-html:pdf") // replace v-html with safer v-dompurify-html approach and custom rules (setup in theme)
            .replace(/v-runtime-template/g, "span"); // make sure v-runtime-template does not get executed inside by simply replacing the tag to a span
        return `<div>${pdfContent}</div>`;
    }

    get isBundle(): boolean {
        return isRecommendationBundles(this.advisor.advice);
    }

    get flowSteps(): ReadonlyArray<FlowStep> {
        return this.advisor.flowStepsNavigation.flowSteps;
    }

    get leadGenFlowSteps(): ReadonlyArray<FlowStep> {
        return this.flowSteps.filter(flowStep => flowStep.type === FlowStepType.LEAD);
    }

    get flowStepsWithoutLeadGen(): ReadonlyArray<FlowStep> {
        return this.flowSteps.filter(flowStep => flowStep.type !== FlowStepType.LEAD);
    }

    get allQuestions(): ReadonlyArray<Question> {
        return this.flowSteps.reduce((accumulatedQuestions, currentFlowStep) => {
            return [...accumulatedQuestions, ...currentFlowStep.questions];
        }, []);
    }

    get leadGenQuestions(): ReadonlyArray<Question> {
        return this.leadGenFlowSteps.reduce((accumulatedQuestions, currentFlowStep) => {
            return [...accumulatedQuestions, ...currentFlowStep.questions.filter(question => !question.isUnanswered && !QuestionTypeChecker.isFileUploadQuestion(question)) || []];
        }, []);
    }

    get questionsWithoutLeadGen(): ReadonlyArray<Question> {
        return this.flowStepsWithoutLeadGen.reduce((accumulatedQuestions, currentFlowStep) => {
            return [...accumulatedQuestions, ...currentFlowStep.questions || []];
        }, []);
    }

    get fileUploadQuestions(): ReadonlyArray<FileUploadQuestion> {
        return this.leadGenFlowSteps.reduce((accumulatedQuestions, currentFlowStep) => {
            return [...accumulatedQuestions, ...currentFlowStep.questions.filter(question => QuestionTypeChecker.isFileUploadQuestion(question))]
        }, []);
    }

    get answeredQuestions(): ReadonlyArray<Question> {
        return this.allQuestions.filter(question => !question.isUnanswered);
    }

    get answeredQuestionsWithoutLeadGen(): ReadonlyArray<Question> {
        return this.questionsWithoutLeadGen.filter(question => !question.isUnanswered);
    }

    get uploadedImageLinks(): ReadonlyArray<string> {
        return this.fileUploadQuestions.reduce((accumulatedLinks, currentQuestion) => {
            return [...accumulatedLinks, ...currentQuestion.fileUrls || []];
        }, []);
    }

    get clusters(): ReadonlyArray<RecommendationClusterExtended> {
        return this.advice &&
            this.advice.currentPage &&
            this.advice.currentPage.clusters &&
            this.advice.currentPage.clusters.map(cluster => ({
            ...cluster,
            ...this.getAdditionalDataForCluster(cluster.clusterNumber)
        }));
    }

    get orderedCategoryInfos(): ReadonlyArray<OrderedCategoryInfo> {
        return this.advice.currentPage.orderedCategoryInfos;
    }

    get bundleClusters(): ReadonlyArray<ReadonlyArray<NullableProductRecommendation>> {
        return this.advice.currentPage.productsBundles.map(bundleProducts => {
            return bundleProducts.filter((product) => product !== null).map((product, index) => {
                product.categoryInfo = this.orderedCategoryInfos[index];
                return product;
            })
        });
    }

    get additionalDataForPerfectMatchesCluster(): AdditionalClusterData {
        return {
            classList: 'cluster-perfect-matches',
            clusterHeadline: this.$t('message-result-first-cluster-header') as string,
            clusterSubHeadline: this.$t('message-result-first-cluster-subheader') as string
        }
    }

    get additionalDataForAlternativesCluster(): AdditionalClusterData {
        return {
            classList: 'cluster-alternatives',
            clusterHeadline: this.$t('message-result-first-cluster-header-alternatives') as string,
            clusterSubHeadline: this.$t('message-result-first-cluster-subheader-alternatives') as string
        }
    }

    get advice(): Recommendation {
        return this.advisor.advice;
    }

    get currentUrl(): string {
        return decodeURIComponent(window.location.href);
    }

    mounted(): void {
        this.$root.$refs.pdfContent = this.$refs.pdfContent;
    }

    questionDataAsHtmlList(questions: ReadonlyArray<Question>): string {
        let html = "<ul class='questions-summary'>";
        questions.forEach((question) => {
            html += `<li class='questions-summary__question questions-summary__question--${question.questionType}'>`;
            html += `<strong class="questions-summary__question-text">${question.questionText}: &nbsp;</strong>`;

            if (QuestionTypeChecker.isChoiceQuestion(question)) {
                html += `<span class="questions-summary__answers">${this.selectedAnswersAsString(question)}</span>`;
            } else if (QuestionTypeChecker.isRangeQuestion(question)) {
                html += `<span class="questions-summary__answers">${question.min} - ${question.max} ${question.unit ? question.unit : ''}`;
            } else if (QuestionTypeChecker.isNumericQuestion(question)) {
                html += `<span class="questions-summary__answers">${(question as NumericValueQuestion).value || '-'} ${question.unit ? question.unit : ''}`;
            } else if (QuestionTypeChecker.isTextQuestion(question)) {
                html += `<span class="questions-summary__answers">${question.answerText}</span>`;
            }
            html += "</li>";
        });
        html += "</ul>";
        return html;
    }

    getAdditionalDataForCluster(clusterNumber: number): AdditionalClusterData {
        if (clusterNumber === 0) {
            return this.additionalDataForPerfectMatchesCluster;
        }
        return this.additionalDataForAlternativesCluster;
    }

    selectedAnswersAsString(question: ChoiceQuestion, separator = ", "): string {
        return question.answers && question.answers.filter(answer => answer.selected).map(answer => answer.answerText).join(separator);
    }

    productPropertyClassName(marking: Marking): string {
        switch (marking) {
            case Marking.POSITIVE:
                return "positive-property";
            case Marking.NEGATIVE:
                return "negative-property";
            case Marking.NEUTRAL:
                return "neutral-property";
            default:
                return "";
        }
    }
}
